import { API, BlockTool } from "@editorjs/editorjs";

/**
 * Type definition for image data.
 */
type ImageData = {
  url: string;
  caption: string;
  width: number;
  height: number;
};

/**
 * ImageBlock is an Editor.js custom block tool for uploading and managing images.
 */
class ImageBlock implements BlockTool {
  api: API;
  data: ImageData;
  container: HTMLElement | null = null;
  readOnly = false;
  uploader: any;
  imgElement: HTMLImageElement | null = null; // Store reference to img
  captionTextElement: HTMLElement | null = null; // Store reference to caption

  static isReadOnlySupported = true;

  constructor({
    data,
    api,
    readOnly,
    config,
  }: {
    data: ImageData | undefined;
    api: API;
    readOnly: boolean;
    config: { uploader: any };
  }) {
    this.api = api;
    this.readOnly = readOnly;
    this.uploader = config.uploader;

    this.data = {
      url: data?.url ?? "",
      caption: data?.caption ?? "",
      width: data?.width ?? 250, // Default width
      height: data?.height ?? 250, // Default height
    };
  }

  static get toolbox() {
    return {
      title: "Image",
      icon: `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
            <rect width="14" height="14" x="5" y="5" stroke="currentColor" stroke-width="2" rx="4"></rect>
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.13968 15.32L8.69058 11.5661C9.02934 11.2036 9.48873 11 9.96774 11C10.4467 11 10.9061 11.2036 11.2449 11.5661L15.3871 16M13.5806 14.0664L15.0132 12.533C15.3519 12.1705 15.8113 11.9668 16.2903 11.9668C16.7693 11.9668 17.2287 12.1705 17.5675 12.533L18.841 13.9634"></path>
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.7778 9.33331H13.7867"></path>
          </svg>`,
    };
  }

  render(): HTMLElement {
    this.container = document.createElement("div");
    this.container.classList.add(
      "flex",
      "flex-col",
      "items-center",
      "mb-5",
      "mt-4"
    );

    if (this.data.url) {
      this._createImageElement(this.data.url, this.data.caption);
    } else {
      this._createFileUploadButton();
    }

    return this.container;
  }

  private _createFileUploadButton() {
    const button = document.createElement("button");
    button.innerText = "Select Image";
    button.classList.add(
      "bg-green-600",
      "text-white",
      "px-4",
      "py-2",
      "rounded",
      "hover:bg-green-700",
      "self-start"
    );
    button.addEventListener("click", () => this._triggerFileInput());
    this.container!.appendChild(button);
  }

  private _triggerFileInput() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";

    input.addEventListener("change", async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        // ✅ Remove the "Select Image" button
        this.container!.innerHTML = "";

        // ✅ Show the loader while uploading
        const loader = this._showLoader();

        try {
          const response = await this.uploader.uploadByFile(file);

          if (response && response.success) {
            this.data.url = response.file.url;

            // ✅ Remove loader and display the image
            loader.remove();
            this._createImageElement(this.data.url, this.data.caption);
          } else {
            throw new Error("Upload failed");
          }
        } catch (error) {
          console.error("Image upload failed:", error);

          // ✅ Remove loader and bring back the "Select Image" button
          loader.remove();
          this._createFileUploadButton();
        }
      }
    });

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  /**
   * Shows a loading spinner.
   */
  private _showLoader(): HTMLElement {
    const loaderWrapper = document.createElement("div");
    loaderWrapper.classList.add(
      "flex",
      "justify-center",
      "items-center",
      "mt-4"
    );

    loaderWrapper.innerHTML = `
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 self"></div>
      <p class="ml-2 text-gray-600">Uploading image...</p>
    `;

    this.container!.appendChild(loaderWrapper);
    return loaderWrapper;
  }

  private _createImageElement(url: string, captionText: string) {
    const wrapper = document.createElement("div");
    wrapper.classList.add(
      "cdx-block",
      "image-tool",
      "image-tool--filled",
      "self-start"
    );

    const img = document.createElement("img");
    img.src = url;
    img.width = this.data.width;
    img.height = this.data.height;
    img.style.width = `${this.data.width}px`;
    img.style.height = `${this.data.height}px`;

    img.classList.add(
      "rounded-md",
      "shadow-md",
      "max-w-full",
      "h-auto",
      "image-tool__image-picture"
    );
    this.imgElement = img; // Store reference

    let caption: HTMLElement;

    caption = document.createElement("p");
    caption.textContent = this.data.caption || "No caption provided";
    caption.classList.add(
      "text-gray-700",
      "italic",
      "text-left",
      "mt-2",
      "image-caption"
    );

    this.captionTextElement = caption;

    // if (this.readOnly) {
    // caption = document.createElement("p");
    // caption.textContent = this.data.caption || "No caption provided";
    // caption.classList.add(
    //   "text-gray-700",
    //   "italic",
    //   "text-left",
    //   "mt-2",
    //   "image-caption"
    // );
    // } else {
    // const captionInput = document.createElement("input");
    // captionInput.type = "text";
    // captionInput.placeholder = "Write a caption...";
    // captionInput.value = captionText || "";
    // captionInput.dispatchEvent(new Event("input"));
    // captionInput.classList.add(
    //   "border",
    //   "border-gray-300",
    //   "rounded",
    //   "p-2",
    //   "w-full",
    //   "mt-2",
    //   "text-left"
    // );

    // captionInput.addEventListener("input", (event: Event) => {
    //   this.data.caption = (event.target as HTMLInputElement).value;
    //   this.infoUpdate();
    // });

    // // ✅ Trigger save when user leaves the caption input
    // captionInput.addEventListener("blur", () => {
    //   this.api.saver.save();
    // });

    // caption = captionInput;
    // }

    wrapper.appendChild(img);
    wrapper.appendChild(caption);

    if (!this.readOnly) {
      const editButton = document.createElement("button");
      editButton.textContent = "Edit Image";
      editButton.className =
        "bg-blue-500 text-white px-4 py-2 rounded mt-3 hover:bg-blue-600";
      editButton.onclick = () => this.openEditor();
      wrapper.appendChild(editButton);
    }

    this.container!.appendChild(wrapper);
  }

  save(): ImageData {
    // Ensure caption input value is stored before saving

    if (this.container) {
      const captionInput = this.container.querySelector(
        "input[type='text']"
      ) as HTMLInputElement;
      if (captionInput) {
        this.data.caption = captionInput.value; // Explicitly update caption before saving
      }
    }

    return {
      url: this.data.url,
      caption: this.data.caption,
      width: this.data.width,
      height: this.data.height,
    };
  }

  infoUpdate(): void {
    this.data.width = parseInt(
      (document.getElementById("imageWidth") as HTMLInputElement).value
    );
    this.data.height = parseInt(
      (document.getElementById("imageHeight") as HTMLInputElement).value
    );

    if (this.imgElement) {
      this.imgElement.width = this.data.width;
      this.imgElement.height = this.data.height;
      this.imgElement.style.width = `${this.data.width}px`;
      this.imgElement.style.height = `${this.data.height}px`;
    }
  }

  openEditor(): void {
    const modal = document.createElement("div");
    modal.innerHTML = `
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white p-6 rounded-lg shadow-lg w-96">
          <h2 class="text-lg font-semibold mb-4">Edit Image</h2>
          <label class="block text-sm font-medium text-gray-700">Width (px)</label>
          <input id="imageWidth" type="number" class="border p-2 w-full mb-3" value="${this.data.width}">
          <label class="block text-sm font-medium text-gray-700">Height (px)</label>
          <input id="imageHeight" type="number" class="border p-2 w-full mb-3" value="${this.data.height}">
          <label class="block text-sm font-medium text-gray-700">Caption</label>
          <input id="captionText" type="text" class="border p-2 w-full mb-3" value="${this.data.caption}">
          <div class="flex justify-end gap-2">
            <button id="saveImage" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">Save</button>
            <button id="closeModal" class="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">Cancel</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const captionInput = document.getElementById(
      "captionText"
    ) as HTMLInputElement;

    captionInput.addEventListener("input", (event: Event) => {
      this.data.caption = (event.target as HTMLInputElement).value;
      this.infoUpdate();

      // ✅ Trigger save when user leaves the caption input
      captionInput.addEventListener("blur", () => {
        this.api.saver.save();
      });

      if (this.captionTextElement) {
        this.captionTextElement.innerHTML = captionInput.value;
      }
    });

    document.getElementById("saveImage")!.addEventListener("click", () => {
      this.infoUpdate();

      modal.remove();
    });

    document
      .getElementById("closeModal")!
      .addEventListener("click", () => modal.remove());
  }
}

export default ImageBlock;
